import { IClientData } from "../models/ClientData";
import { IAnonClientData } from "../models/AnonClientData";
import { getFlattenedData } from "./SurveyJSHelpers";

export type SessionDraftPayload = {
  data: IClientData;
  updatedAt: string;
};

export function parseUpdatedAt(value?: string): number {
  const ms = Date.parse(value || "");
  return Number.isFinite(ms) ? ms : 0;
}

// Ties favor session - if both were written at the same instant, prefer what the
// user already has locally rather than clobbering it with the API payload.
export function chooseLatest(
  sessionPayload: SessionDraftPayload | null,
  apiPayload: SessionDraftPayload | null
): SessionDraftPayload | null {
  if (sessionPayload && !apiPayload) return sessionPayload;
  if (apiPayload && !sessionPayload) return apiPayload;
  if (!sessionPayload && !apiPayload) return null;

  const sessionMs = parseUpdatedAt(sessionPayload?.updatedAt);
  const apiMs = parseUpdatedAt(apiPayload?.updatedAt);

  return sessionMs >= apiMs ? sessionPayload! : apiPayload!;
}

// One identity per person: anyone not signed in to a real account - including an anonymous
// Firebase session started mid-journey - is "anon", so their draft key never changes.
export function getDraftUserId(user?: { uid?: string; isAnonymous?: boolean } | null): string {
  return user?.uid && !user.isAnonymous ? user.uid : "anon";
}

// sessionStorage is already per tab, so the key needs no tab id.
export function buildDraftKey(userId: string): string {
  return `draft:${userId.trim() || "anon"}`;
}

export function readSessionDraft(key: string): SessionDraftPayload | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionDraftPayload;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      data: parsed.data ?? {} as IClientData,
      updatedAt: parsed.updatedAt ?? "",
    };
  } catch {
    return null;
  }
}

function writeSessionDraft(key: string, payload: SessionDraftPayload) {
  try {
    sessionStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // do nothing if error
  }
}

// Every write is a merge of the fields the writer owns: nothing can remove another writer's
// fields. An undefined value removes that field (e.g. a form answer the user cleared).
export function mergeSessionDraft(
  key: string,
  patch: Record<string, unknown>,
  updatedAt: string = new Date().toISOString()
): void {
  const data: Record<string, unknown> = { ...(readSessionDraft(key)?.data ?? {}) };
  Object.entries(patch).forEach(([field, value]) => {
    if (value === undefined) {
      delete data[field];
    } else {
      data[field] = value;
    }
  });
  writeSessionDraft(key, { data: data as IClientData, updatedAt });
}

// Clearing isn't an edit: the remaining fields keep their timestamp, and an emptied draft is removed so it can't
// outrank the API data on the next restore.
export function clearSessionDraftFields(key: string, fields: string[]): void {
  const existing = readSessionDraft(key);
  if (!existing) return;
  mergeSessionDraft(key, Object.fromEntries(fields.map((field) => [field, undefined])), existing.updatedAt);
  if (Object.keys(readSessionDraft(key)?.data ?? {}).length === 0) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore quota / transient errors
    }
  }
}

const serverOwnedFields = ["id", "transferToID"] as const;

// A save response echoes what was sent, so it never writes form answers back: only the ids the server assigns.
export function reconcileSessionDraftWithApiResponse(
  key: string,
  apiData: IClientData | undefined | null
): void {
  if (!apiData) return;
  const patch: Record<string, unknown> = {};
  serverOwnedFields.forEach((field) => {
    if (apiData[field] !== undefined && apiData[field] !== null) patch[field] = apiData[field];
  });
  if (Object.keys(patch).length > 0) mergeSessionDraft(key, patch);
}

// Seeds answers the draft doesn't have yet (e.g. a new tab); never overwrites what the user entered here.
export function reconcileSessionDraftWithAnonClientResponse(
  key: string,
  anonData: IAnonClientData | undefined | null
): void {
  if (!anonData) return;
  const existing = (readSessionDraft(key)?.data ?? {}) as Record<string, unknown>;
  const server = getFlattenedData({
    firstName: anonData.firstName,
    lastName: anonData.lastName,
    email: anonData.email,
    primaryPhone: anonData.primaryPhone,
    dateOfBirth: anonData.dateOfBirth,
    postcode: anonData.postcode,
  }) as Record<string, unknown>;
  const missing = Object.fromEntries(
    Object.entries(server).filter(([field, value]) => value != null && value !== "" && existing[field] == null)
  );
  if (Object.keys(missing).length > 0) mergeSessionDraft(key, missing);
}

// Clears all session draft entries (for security on logout or form save)

export function clearAllSessionDrafts() {
  const keysToRemove: string[] = [];
  const draftKeyPattern = /^draft:/;

  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && draftKeyPattern.test(key)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore quota / transient errors
    }
  });
}


export function cleanupDraftsForOtherUsers(currentUserId: string | null | undefined): void {
  if (!currentUserId) return;

  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (!key) continue;

    // Extract userId from key pattern: draft:<anon|uid>; signing in leaves the anonymous draft behind too
    const match = key.match(/^draft:(.+)$/);
    if (!match) continue;

    const keyUserId = match[1];

    if (keyUserId !== currentUserId) {
      try {
        sessionStorage.removeItem(key);
      } catch {
      }
    }
  }
}

// clean up logged out quote results
export function clearAnonymousQuoteSessionData(): void {
  const keysToRemove = [
    "loggedOutQuickQuoteReason",
    "loggedOutQuickQuoteEmail",
    "loggedOutQuickQuoteResults",
  ];

  keysToRemove.forEach((key) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore quota / transient errors
    }
  });
}

export function clearAppointmentBookingSessionData(): void {
  const appointmentBookingKeyPattern = /^appointment-booking:/;
  const keysToRemove: string[] = [];

  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && appointmentBookingKeyPattern.test(key)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
    }
  });
}
